import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Bell, CheckCircle2, ChevronDown, ChevronRight, Clock3, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import BusinessActionContract from '../business/BusinessActionContract';
import { alertResolutionContract } from '../../lib/businessContracts';
import { formatDateHeure } from '../../lib/formats';
import { libelle } from '../../lib/libelles';

// Une escalade signale une attente prolongée, pas une gravité critique.
export const alertTone = (event) =>
  ['error', 'critical'].includes(event.severity) ? 'critical'
    : event.escalated || event.severity === 'warning' ? 'attention' : 'info';

const severityLabel = (event) => event.severity === 'critical' ? 'Critique' : libelle('alerteSeverity', event.severity, 'Information');

export const waitingLabel = (event) => {
  if (!event.awaiting_decision || event.waiting_hours == null) return null;
  const hours = Math.max(0, Number(event.waiting_hours));
  if (!Number.isFinite(hours)) return null;
  return hours < 1 ? 'Moins de 1 h' : hours < 24 ? `${hours} h d’attente` : `${Math.floor(hours / 24)} j d’attente`;
};

export function AlertRow({ event, selected, onSelect }) {
  const tone = alertTone(event);
  const Icon = tone === 'critical' ? ShieldAlert : Bell;
  return (
    <li>
      <button type="button" className="alert-inbox-row" data-selected={selected} aria-pressed={selected}
        data-testid={`alert-row-${event.id}`} onClick={(e) => onSelect(event.id, e.currentTarget)}>
        <span className="alert-symbol" data-tone={tone}><Icon size={18} aria-hidden="true" /></span>
        <span className="alert-row-body">
          <span className="alert-row-meta">
            <span>{libelle('alerteCategorie', event.ui.category, 'Autres')}</span>
            {event.escalated && <span className="alert-escalated">Escaladée</span>}
            <span className="alert-priority" data-tone={tone}>{severityLabel(event)}</span>
          </span>
          <span className="alert-row-title">{event.ui.label}</span>
          <span className="alert-row-summary" data-testid={`alert-summary-${event.id}`}>{event.ui.cause}</span>
          <span className="alert-row-date">
            <Clock3 size={12} aria-hidden="true" />
            <time dateTime={event.created_at}>{event.created_at ? formatDateHeure(event.created_at) : 'Date inconnue'}</time>
            {waitingLabel(event) && <span>· {waitingLabel(event)}</span>}
          </span>
        </span>
        <ChevronRight className="alert-row-chevron" size={16} aria-hidden="true" />
      </button>
    </li>
  );
}

export function AlertDetail({ event, view, decisions, target, onOpenTarget, onDecide, onConfirm, busy, error }) {
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState('');
  const confirmationTitle = useRef(null);
  useEffect(() => { if (pending) confirmationTitle.current?.focus(); }, [pending]);
  const tone = alertTone(event);
  // Phrase déjà rédigée en français par le serveur (pas un code de liste).
  const etapeConseillee = event.ui.action;
  const resolved = view === 'resolved';
  const closing = pending && ['resolve', 'ignore'].includes(pending.type);
  return (
    <div className="alert-detail-content">
      <div className="alert-detail-heading">
        <span className="alert-detail-eyebrow">{resolved ? 'Dossier clôturé' : 'Dossier à examiner'}</span>
        <span className="alert-priority" data-tone={tone}>{severityLabel(event)}</span>
      </div>
      <h2 className="alert-detail-title">{event.ui.label}</h2>
      <p className="alert-detail-date">{event.created_at ? formatDateHeure(event.created_at) : 'Date inconnue'}</p>
      {event.escalated && <p className="alert-escalation-note"><Clock3 size={16} aria-hidden="true" />
        Cette alerte a été remontée après une attente prolongée.
      </p>}
      <dl className="alert-detail-facts">
        <div><dt>Ce qui se passe</dt><dd>{event.ui.cause}</dd></div>
        <div><dt>Impact sur vos opérations</dt><dd>{event.ui.impact}</dd></div>
        <div className="alert-recommendation"><dt><ArrowRight size={14} aria-hidden="true" /> Prochaine étape conseillée</dt><dd>{etapeConseillee}</dd></div>
      </dl>
      {target && <Button variant="outline" className="w-full justify-between" onClick={onOpenTarget} data-testid={`alert-open-entity-${event.id}`}>
        {target.label}<ArrowRight size={16} aria-hidden="true" />
      </Button>}
      {/* Une étape proposée se confirme en un geste: le trajet avance et l'alerte se referme. */}
      {event.event_type === 'trip_status_proposed' && !resolved && onConfirm && (
        <Button className="w-full" onClick={() => onConfirm(event)} disabled={busy} data-testid={`alert-confirm-step-${event.id}`}>
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" /> : <CheckCircle2 size={16} className="mr-2" aria-hidden="true" />}
          Confirmer l’étape
        </Button>
      )}
      <BusinessActionContract title="Règles de résolution" contract={alertResolutionContract(event)} compact />
      {resolved ? (
        <div className="alert-closed"><CheckCircle2 size={18} aria-hidden="true" /><span>Clôturée {event.resolved_at ? `le ${formatDateHeure(event.resolved_at)}` : '— date inconnue'}.<br />La décision reste tracée dans le dossier.</span></div>
      ) : (
        <section className="alert-decisions" aria-label="Décider de la suite">
          <div className="alert-decision-label"><span>Décider de la suite</span><span>Décision tracée</span></div>
          {pending ? (
            <form className="alert-confirmation" onSubmit={(e) => { e.preventDefault(); onDecide(event, pending, note); }}>
              <h3 ref={confirmationTitle} tabIndex={-1}>{pending.label}</h3>
              <p>{closing ? 'L’alerte quittera cette file et rejoindra l’historique.' : 'L’alerte passera « En cours » pour poursuivre le suivi.'}
                {pending.type === 'call_driver' && ' Cette action note une demande d’appel ; elle ne lance pas un appel téléphonique.'}</p>
              <label htmlFor={`alert-note-${event.id}`}>Note de suivi <span>(facultatif)</span></label>
              <textarea id={`alert-note-${event.id}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} disabled={busy} placeholder="Précisez le contexte ou la suite à donner…" />
              {error && <p role="alert" className="alert-decision-error">{error}</p>}
              <div className="alert-confirm-buttons">
                <Button type="button" variant="ghost" onClick={() => setPending(null)} disabled={busy}>Annuler</Button>
                <Button type="submit" disabled={busy}>{busy && <Loader2 size={16} className="mr-2 animate-spin" aria-hidden="true" />}{busy ? 'Enregistrement…' : 'Confirmer la décision'}</Button>
              </div>
            </form>
          ) : (
            <div className="alert-action-buttons">
              <Button onClick={() => setPending(decisions.find((d) => d.type === 'resolve'))} disabled={busy}><CheckCircle2 size={16} className="mr-2" aria-hidden="true" />Résoudre</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" disabled={busy}>Autre décision<ChevronDown size={15} className="ml-2" aria-hidden="true" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-56">
                  {decisions.filter((d) => d.type !== 'resolve').map((decision) => {
                    const Icon = decision.icon;
                    return <DropdownMenuItem key={decision.type} className="min-h-11 cursor-pointer" onSelect={() => setPending(decision)}><Icon aria-hidden="true" />{decision.label}</DropdownMenuItem>;
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
